import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { checklistQueriesToScreenerTokens } from '~/utilities/checklistQueryParse';
import { buildSignalQueriesPreference } from '../data/signalScreenerQueries';

export interface SelectedSignalsCountState {
  total: number;
  isLoading: boolean;
  error: boolean;
}

const EMPTY_STATE: SelectedSignalsCountState = {
  total: 0,
  isLoading: false,
  error: false,
};

/**
 * Live screener match count for the current signal selection.
 * Multiple signals are combined with AND (same as the company screener).
 */
export function useOnboardingSelectedSignalsCount(
  signalIds: string[],
  isActive: boolean,
): SelectedSignalsCountState {
  const selectionKey = signalIds.join(',');
  const [state, setState] = useState<SelectedSignalsCountState>(EMPTY_STATE);
  const cacheRef = useRef<Record<string, SelectedSignalsCountState>>({});

  useEffect(() => {
    if (!isActive || signalIds.length === 0) {
      setState(EMPTY_STATE);
      return;
    }

    const cached = cacheRef.current[selectionKey];
    if (cached && !cached.isLoading && !cached.error) {
      setState(cached);
      return;
    }

    const abortController = new AbortController();
    const loadingState: SelectedSignalsCountState = {
      total: cached?.total ?? 0,
      isLoading: true,
      error: false,
    };
    cacheRef.current[selectionKey] = loadingState;
    setState(loadingState);

    const fetchCount = async () => {
      try {
        const queries = buildSignalQueriesPreference(signalIds);
        const tokens = checklistQueriesToScreenerTokens(queries);
        if (tokens.length === 0) {
          throw new Error('Invalid screener query');
        }

        const response = await axios.post<{ total?: number }>(
          '/api/company-screener',
          {
            data: tokens,
            limit: 1,
            offset: 0,
            sort_by: 'market_cap',
            sort_direction: 'desc',
          },
          { signal: abortController.signal },
        );

        if (abortController.signal.aborted) {
          return;
        }

        const total = Number(response.data?.total ?? 0);
        const nextState: SelectedSignalsCountState = {
          total: Number.isFinite(total) ? total : 0,
          isLoading: false,
          error: false,
        };
        cacheRef.current[selectionKey] = nextState;
        setState(nextState);
      } catch (err: unknown) {
        if (abortController.signal.aborted || axios.isCancel(err)) {
          return;
        }

        const nextState: SelectedSignalsCountState = {
          total: 0,
          isLoading: false,
          error: true,
        };
        cacheRef.current[selectionKey] = nextState;
        setState(nextState);
      }
    };

    void fetchCount();

    return () => abortController.abort();
  }, [isActive, selectionKey, signalIds]);

  return state;
}
